import { Button } from '@brighterion/br-ui';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import { callAll, handleKeyEvent, handleOnClickEvent } from 'Utils';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import PageHeader from 'Components/PageHeader';
import ButtonGroup from 'Components/ButtonGroup';
import MultiOptionDropdown from 'Components/MultiOptionDropdown';

import { useFormikContext } from 'formik';
import { CREATE_VELOCITY_PARAM_INPUTS, Status } from 'Common/parameterEnums';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import styles from './Header.css';

type HeaderProps = {
    handleCancel: () => void;
    mode: 'create' | 'edit';
};
const Header: React.FC<HeaderProps> = ({ handleCancel, mode }) => {
    const { t } = useTranslation();
    const { addTechnicalErrorModal, clearAll } = useReduxModal();
    const {
        isSubmitting,
        initialValues,
        dirty,
        isValid,
        handleSubmit,
        setFieldValue
    } = useFormikContext<ParameterVelocityFormSchema>();
    const disableSave = !dirty || !isValid || isSubmitting;

    const displayConfirmationModal = () => {
        addTechnicalErrorModal('', t('Save As Ready To Deploy'), clearAll, {
            id: 'createVelocityForm-saveAsReadyToDeploy-modal',
            status: 'warning',
            content: (
                <Trans i18nKey='SaveAsReadyToDeployModalContent'>
                    Are you sure you want to save this parameter as <strong>Ready To Deploy</strong>?
                    <br />
                    <br />
                    Once marked as Ready To Deploy, this parameter can no longer be edited and will be deployed as part
                    of the next rules deployment. This action cannot be undone.
                </Trans>
            ),
            onActionText: t('Save As Ready To Deploy'),
            onAction: () => {
                handleSubmit();
                clearAll();
            },
            cancelText: t('Cancel')
        });
    };

    const handleTrackEventClick = (operation: string) => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: `Parameters - ${operation}`,
                text: operation
            }
        });
    };

    const handleMenuTrackEventClick = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Parameters - Edit Parameter',
                text: 'Edit Parameter'
            }
        });
    };

    const buttonOptions: MenuItem[] = [
        {
            visibility: true,
            displayText: t('Draft'),
            handleNavigation: callAll(
                () => setFieldValue(CREATE_VELOCITY_PARAM_INPUTS.STATUS, Status.DRAFT, false),
                () => handleTrackEventClick('Add New Velocity as Draft'),
                handleSubmit
            )
        },
        {
            visibility: true,
            displayText: t('Ready To Deploy'),
            handleNavigation: callAll(
                () => setFieldValue(CREATE_VELOCITY_PARAM_INPUTS.STATUS, Status.READY_TO_DEPLOY, false),
                () => handleTrackEventClick('Add New Velocity as Ready to Deploy'),
                displayConfirmationModal
            )
        }
    ];

    return (
        <PageHeader>
            <PageHeader.Left className={styles.pageHeaderLeft}>
                {t(mode === 'create' ? 'New Velocity' : 'EditVelocityBreadCrumb', {
                    parameterName: initialValues.displayName
                })}
                <span className={styles.pagesubHeaderLeft}>
                    {t(`ParameterPageHeaderStatus.${initialValues.status}`)}
                </span>
            </PageHeader.Left>
            <PageHeader.Right>
                <ButtonGroup direction='row'>
                    <Button
                        dataTestId='create-velocity-form-cancel-button'
                        disabled={isSubmitting}
                        onKeyDown={handleKeyEvent(handleCancel)}
                        onClick={handleOnClickEvent(handleCancel)}
                        size='medium'
                        variant='tertiary'
                    >
                        {t('Cancel')}
                    </Button>

                    <MultiOptionDropdown
                        id='create-velocity-multi-option-button'
                        dataTestId='create-velocity-multi-option-button'
                        variant='primary'
                        showArrows
                        options={buttonOptions}
                        baseText={t('Save As')}
                        alignment='right'
                        disabled={disableSave}
                        callback={handleMenuTrackEventClick}
                    />
                </ButtonGroup>
            </PageHeader.Right>
        </PageHeader>
    );
};

export default React.memo(Header);
